import { CAccordion, CAccordionBody, CAccordionHeader, CAccordionItem, CButton, CForm, CModal, CModalBody } from '@coreui/react-pro';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { Fragment, useState } from 'react';
import { useDispatch } from 'react-redux';

import { LOCAL_ORG_USER, MAXIMUM_EMAIL_INVITE_SEAT, SEAT_STEPS } from '../../../../../constants';
import { exportToCSV, tryParseJSON } from '../../../../../utils';

import images from '../../../../../assets/images';
import { SimpleTextAreaGroup } from '../../../../General/Forms';
import Waiting from '../../../../General/Loadings/Waiting';

import { useParams } from 'react-router-dom';
import { setShow } from '../../../../../actions/general';
import { assignMultipleSeatsRequest } from './actions';

const AssignMultipleUsers = ({ seats, productId, openSeats, setPagination, fetchOrgSeats }) => {
     const dispatch = useDispatch();
     const { courseId } = useParams();

     const [currentStep, setCurrentStep] = useState(SEAT_STEPS.ASSIGN_MULTIPLE_SEAT);
     const [visibleCollapse, setVisibleCollapse] = useState(false);
     const [isSubmitting, setIsSubmitting] = useState(false);
     const [msgValidateEmails, setMsgValidateEmails] = useState('');
     const [seatAssignmentResults, setSeatAssignmentResults] = useState({
          successfully: {
               label: 'added successfully',
               color: 'success',
               items: [],
          },
          failed: {
               label: 'failed',
               color: 'danger',
               items: [],
          },
          hasAwaitingItem: {
               label: 'already has an awaiting item',
               items: [],
               class: 'result-alreadyAssigned',
          },
          alreadyAssigned: {
               label: 'already assigned to seats',
               items: [],
               class: 'result-alreadyAssigned',
          },
          alreadyAccessCourse: {
               label: 'already have access to this course',
               color: 'primary',
               items: [],
          },
          alreadyCompletedCourse: {
               label: 'have already completed this course',
               color: 'primary',
               items: [],
          },
     });

     const orgUserLocalStorage = tryParseJSON(localStorage.getItem(LOCAL_ORG_USER));

     const initialValues = {
          emails: '',
     };

     const { organizationId } = orgUserLocalStorage;

     const isAssignMultipleSeat = currentStep === SEAT_STEPS.ASSIGN_MULTIPLE_SEAT;

     const isLoadingOpenSeat = openSeats === -1;

     const validationAssignMultipleSeat = (emails) => {
          const emailsFinal = emails
               .replace(/\n/g, ',')
               .split(',')
               .filter((email) => email.trim() !== '');

          const emailsFinalLength = emailsFinal.length;

          if (!emailsFinalLength) {
               setMsgValidateEmails('Field is required.');
               return true;
          }

          if (emailsFinalLength > MAXIMUM_EMAIL_INVITE_SEAT) {
               setMsgValidateEmails(`Maximum ${MAXIMUM_EMAIL_INVITE_SEAT} email addresses allowed to add per time.`);
               return true;
          }

          if (emailsFinalLength > openSeats) {
               setMsgValidateEmails('Addition of emails failed because of exceeding the available emails.');
               return true;
          }

          setMsgValidateEmails('');
          return false;
     };

     const handleCancelButtonClick = () => {
          dispatch(setShow(null));
     };

     const handleCollapse = () => {
          setVisibleCollapse(!visibleCollapse);
     };

     const handleAssignMultipleUsers = (values) => {
          if (validationAssignMultipleSeat(values.emails)) {
               return;
          }

          const emails = values.emails
               .replace(/\n/g, ',')
               .split(',')
               .filter((email) => email.trim() !== '')
               .map((email) => email.toLowerCase());

          const newValues = {
               organizationId,
               organizationProductId: courseId,
               seats,
               emails,
          };

          const nextDispatch = {
               productId,
               setCurrentStep,
               setSeatAssignmentResults,
               setIsSubmitting,
               setPagination,
               fetchOrgSeats,
          };

          dispatch(assignMultipleSeatsRequest(newValues, nextDispatch));
     };

     const handleDownloadResultClick = () => {
          const fileName = `Seat Assignment Result - ${new Date().getTime()}`;

          const seatAssignmentArr = Object.entries(seatAssignmentResults);
          const seatAssignmentLength = seatAssignmentArr.length;
          const manySeatAssignmentRows = [];

          for (let i = 0; i < seatAssignmentLength; i++) {
               const [key, seatResult] = seatAssignmentArr[i];
               const seatResultItems = seatResult.items;

               let statusText = '';

               switch (key) {
                    case 'successfully':
                         statusText = 'successfully added';
                         break;

                    case 'failed':
                         statusText = 'failed to be added due to error';
                         break;

                    case 'hasAwaitingItem':
                         statusText = 'skipped: already has awaiting item';
                         break;

                    case 'alreadyAssigned':
                         statusText = 'skipped: already assigned to a seat';
                         break;

                    case 'alreadyAccessCourse':
                         statusText = 'skipped: already has access';
                         break;

                    case 'alreadyCompletedCourse':
                         statusText = 'skipped: already completed course';
                         break;

                    default:
                         break;
               }

               for (let j = 0; j < seatResultItems.length; j++) {
                    const seatResultItem = seatResultItems[j];

                    manySeatAssignmentRows.push([seatResultItem, statusText]);
               }
          }

          const dataToExport = [['Email', 'Status'], ...manySeatAssignmentRows];

          exportToCSV(`${fileName}.csv`, dataToExport);
     };

     return (
          <CModal alignment="center" visible className="modal-assign-seat">
               <CModalBody>
                    <div className="modal-heading">
                         <div className="icon-group d-flex">
                              <img src={images.IconManPeopleOrganizationUser} className="icon-item" alt="Assign Seat" />
                              <img src={images.IconAuthorizeArrowRight} className="icon-item" alt="Arrow Seat" />
                              <div className="icon-shape">
                                   <img src={images.IconShape} className="icon-item" alt="Icon Seat" />
                                   <img src={images.IconShape} className="icon-item" alt="Icon Seat" />
                                   <img src={images.IconShape} className="icon-item" alt="Icon Seat" />
                              </div>
                         </div>
                         <h4 className="modal-title">
                              {isAssignMultipleSeat ? SEAT_STEPS.ASSIGN_MULTIPLE_SEAT : SEAT_STEPS.SEAT_ASSIGNMENT_COMPLETE}
                         </h4>

                         {isAssignMultipleSeat ? (
                              <>
                                   <p className="modal-description">
                                        Enter up to 50 comma-separated or line-separated email addresses in the box below and we will assign them to
                                        open seats. You can include new and existing users.
                                   </p>
                                   <p className="modal-caption mb-0">
                                        You have {isLoadingOpenSeat ? `...` : openSeats} available {openSeats > 1 ? 'seats' : 'seat'}
                                   </p>
                              </>
                         ) : (
                              <p className="modal-caption mb-0">Here are the results from your bulk seat assignment.</p>
                         )}
                    </div>
                    <Formik initialValues={initialValues} validateOnBlur={false} onSubmit={handleAssignMultipleUsers}>
                         {({ values, setFieldValue, handleSubmit }) => {
                              const isDisableButtonInviteUser = isSubmitting || isLoadingOpenSeat || !!msgValidateEmails;

                              const handleChangeEmailsInput = (e) => {
                                   const emailValues = e.target.value;

                                   setFieldValue('emails', emailValues);
                                   validationAssignMultipleSeat(emailValues);
                              };

                              return (
                                   <>
                                        {isAssignMultipleSeat ? (
                                             <CForm onSubmit={handleSubmit} noValidate>
                                                  <div className="d-flex flex-column form-group">
                                                       <SimpleTextAreaGroup
                                                            id="emails"
                                                            placeholder="Paste email list here"
                                                            className="email-seats text-left"
                                                            value={values.emails}
                                                            error={msgValidateEmails}
                                                            rows="8"
                                                            isRequired
                                                            disabled={isSubmitting || isLoadingOpenSeat}
                                                            handleChange={handleChangeEmailsInput}
                                                       />

                                                       <CButton
                                                            className="btn btn-blue-dark text-uppercase"
                                                            disabled={isDisableButtonInviteUser}
                                                            type="submit"
                                                       >
                                                            <Waiting isLoading={isSubmitting}>Invite Users</Waiting>
                                                       </CButton>
                                                  </div>

                                                  <div className="d-flex justify-content-center">
                                                       <span
                                                            className="btn btn-link btn-reset-password text-uppercase"
                                                            onClick={handleCancelButtonClick}
                                                       >
                                                            Cancel
                                                       </span>
                                                  </div>
                                             </CForm>
                                        ) : (
                                             <>
                                                  <div className="d-flex flex-column form-group">
                                                       <CAccordion className="accordion-training-content training-accordion">
                                                            {Object.entries(seatAssignmentResults).map(([key, seatAssignment]) => {
                                                                 const seatAssignmentItemLength = seatAssignment.items.length;
                                                                 const seatAssignmentLabel = `${seatAssignmentItemLength} ${
                                                                      seatAssignmentItemLength > 1 ? 'users' : 'user'
                                                                 } ${seatAssignment.label}`;

                                                                 return (
                                                                      <Fragment key={key}>
                                                                           {seatAssignmentItemLength > 0 && (
                                                                                <CAccordionItem itemKey={key} onClick={handleCollapse}>
                                                                                     <CAccordionHeader className="d-flex justify-content-between align-items-center">
                                                                                          <div className="d-flex justify-content-between w-100 me-2">
                                                                                               <span
                                                                                                    className={`accordion-title text-${seatAssignment.color} ${seatAssignment.class}`}
                                                                                               >
                                                                                                    {seatAssignmentLabel}
                                                                                               </span>
                                                                                               <span className="accordion-see-details">
                                                                                                    See Details
                                                                                               </span>
                                                                                          </div>
                                                                                     </CAccordionHeader>

                                                                                     <CAccordionBody>
                                                                                          <ul className="accordion-content-list text-left mb-0 scrollbar-custom">
                                                                                               {seatAssignmentItemLength > 0 &&
                                                                                                    seatAssignment.items.map((result, index) => (
                                                                                                         <li
                                                                                                              key={index}
                                                                                                              className="accordion-content-item"
                                                                                                         >
                                                                                                              {result}
                                                                                                         </li>
                                                                                                    ))}
                                                                                          </ul>
                                                                                     </CAccordionBody>
                                                                                </CAccordionItem>
                                                                           )}
                                                                      </Fragment>
                                                                 );
                                                            })}
                                                       </CAccordion>

                                                       <CButton className="btn btn-blue-dark text-uppercase" onClick={handleCancelButtonClick}>
                                                            Done
                                                       </CButton>
                                                  </div>

                                                  <div className="d-flex justify-content-center">
                                                       <span
                                                            className="btn btn-link btn-reset-password text-uppercase"
                                                            onClick={handleDownloadResultClick}
                                                       >
                                                            Download results
                                                       </span>
                                                  </div>
                                             </>
                                        )}
                                   </>
                              );
                         }}
                    </Formik>
               </CModalBody>
          </CModal>
     );
};

AssignMultipleUsers.propTypes = {
     seats: PropTypes.array,
     productId: PropTypes.string,
     openSeats: PropTypes.number,
     setPagination: PropTypes.func,
     fetchOrgSeats: PropTypes.func,
};

export default AssignMultipleUsers;
